import React, { FunctionComponent, useState } from "react"
import { useTranslation } from "react-i18next"

import Select from "components/Select/Select"
import { LmidModalMode } from "components/LmidModal/lmidModalMode"
import { allowedLmidModes } from "./copyAssetMatrix"
import { ICopyAssetLmidOverwrite, ICopyAssetRow, ICopyAssetState } from "./copyAssetRows"
import CopyAssetLmidSearch from "./CopyAssetLmidSearch"
import { IApiLink } from "@/types"
import { AssetStatus } from "models/Asset"

export interface ICopyAssetLmidFieldProps {
    state: ICopyAssetState
    row: ICopyAssetRow
    rowNumber: number
    /** Effective status of the copy, constraining the offered LMID modes. */
    status: AssetStatus
    isInvalid: boolean
    articleNumber: string
    isInternalArticle: boolean
    takenLmids: string[]
    takenLmidCopyNumbers: Record<string, number>
    onSetLmidMode(lmidMode: LmidModalMode): void
    onAssignLmid(lmid: string, createAssetLink: IApiLink | null, overwrite: ICopyAssetLmidOverwrite | null): void
}

/**
 * The LMID cell of a copy card, one control with two faces: while it is empty
 * it is the dropdown of the LMID modes, and as soon as something is typed or
 * pasted (or an LMID is assigned) it becomes the LMID lookup of the add-asset
 * flow, looking a pasted value up right away. Clearing the lookup drops the
 * assignment and returns to the dropdown.
 */
const CopyAssetLmidField: FunctionComponent<React.PropsWithChildren<ICopyAssetLmidFieldProps>> = ({
    state,
    row,
    rowNumber,
    status,
    isInvalid,
    articleNumber,
    isInternalArticle,
    takenLmids,
    takenLmidCopyNumbers,
    onSetLmidMode,
    onAssignLmid,
}) => {
    const { t } = useTranslation()
    const [text, setText] = useState<string | null>(null)

    const showSearch = row.lmidMode === LmidModalMode.enterLmid && (text !== null || !!row.lmid)

    function activateSearchOnTyping(event: React.KeyboardEvent) {
        if (row.lmidMode !== LmidModalMode.enterLmid) return
        if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return
        event.preventDefault()
        event.stopPropagation()
        setText(event.key)
    }

    function activateSearchOnPaste(event: React.ClipboardEvent) {
        if (row.lmidMode !== LmidModalMode.enterLmid) return
        const pasted = event.clipboardData.getData("text").trim()
        if (!pasted) return
        event.preventDefault()
        event.stopPropagation()
        setText(pasted)
    }

    function handleSearchChange(value: string) {
        if (!value.trim()) {
            setText(null)
            if (row.lmid) onAssignLmid("", null, null)
            return
        }
        setText(value)
        if (row.lmid && value !== row.lmid) onAssignLmid("", null, null)
    }

    function handleSearchReset() {
        setText(null)
        if (row.lmid) onAssignLmid("", null, null)
    }

    if (showSearch) {
        return (
            <CopyAssetLmidSearch
                articleNumber={articleNumber}
                isInternalArticle={isInternalArticle}
                takenLmids={takenLmids}
                takenLmidCopyNumbers={takenLmidCopyNumbers}
                value={text ?? row.lmid}
                isInvalid={isInvalid}
                autoFocus={text !== null}
                testId={`copy-asset-lmid-input-${rowNumber}`}
                onChange={handleSearchChange}
                onReset={handleSearchReset}
                onAssign={(lmid, createAssetLink, overwrite) => {
                    onAssignLmid(lmid, createAssetLink, overwrite)
                    setText(null)
                }}
            />
        )
    }

    return (
        <div onKeyDownCapture={activateSearchOnTyping} onPasteCapture={activateSearchOnPaste}>
            <Select
                value={row.lmidMode}
                options={allowedLmidModes(state.sourceKind, state.ownershipType, status).map(mode => ({
                    label: t(`lmidModal.${mode}`),
                    value: mode,
                }))}
                onChange={option => option && onSetLmidMode(option.value)}
                isInvalid={isInvalid}
                isSearchable={false}
                isInModal
                menuWidth="26rem"
                testIdPrefix={`copy-asset-lmid-mode-${rowNumber}`}
            />
        </div>
    )
}

export default CopyAssetLmidField
